import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatLogTimestamp } from '../../utils/dateUtils';
import './InstanceLogs.css';

interface InstanceLogsProps {
  kgId: string;
  versionId: string;
  instanceId: string;
  onClose: () => void;
}

interface LogEvent {
  timestamp: number;
  message: string;
  ingestionTime: number;
}

type LogLevel = 'INFO' | 'WARN' | 'ERROR' | 'DEBUG' | 'OTHER';
type LevelFilter = 'ALL' | LogLevel;

interface ParsedLog {
  raw: LogEvent;
  level: LogLevel;
  message: string;
  timestamp: number;
  collapsed?: number; // count if this is a collapsed progress group
}

const LEVEL_RE: Record<LogLevel, RegExp> = {
  ERROR: /\b(error|exception|traceback|failed|failure)\b/i,
  WARN:  /\b(warn|warning|deprecated)\b/i,
  DEBUG: /\b(debug|verbose|trace)\b/i,
  INFO:  /\b(info|success|complete|done|start)\b/i,
  OTHER: /(?:)/,
};

function parseLevel(msg: string): LogLevel {
  for (const lvl of ['ERROR', 'WARN', 'DEBUG', 'INFO'] as LogLevel[]) {
    if (LEVEL_RE[lvl].test(msg)) return lvl;
  }
  return 'OTHER';
}

const PROGRESS_RE = /\d+%|\d+\/\d+|\bprogress\b|\bprocessing\b/i;

function parseLogs(events: LogEvent[]): ParsedLog[] {
  const result: ParsedLog[] = [];
  let i = 0;
  while (i < events.length) {
    const ev = events[i];
    // Collapse consecutive progress-like lines (3+) into one
    if (PROGRESS_RE.test(ev.message)) {
      let j = i + 1;
      while (j < events.length && PROGRESS_RE.test(events[j].message)) j++;
      const count = j - i;
      if (count >= 3) {
        result.push({
          raw: ev,
          level: 'INFO',
          message: ev.message,
          timestamp: ev.timestamp,
          collapsed: count,
        });
        i = j;
        continue;
      }
    }
    result.push({
      raw: ev,
      level: parseLevel(ev.message),
      message: ev.message,
      timestamp: ev.timestamp,
    });
    i++;
  }
  return result;
}

function highlight(text: string, term: string): React.ReactNode {
  if (!term) return text;
  const re = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(re);
  return parts.map((p, i) =>
    re.test(p) ? <mark key={i} className="il-search-mark">{p}</mark> : p
  );
}

function copyToClipboard(text: string) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

function downloadLogs(logs: LogEvent[], instanceId: string) {
  const content = logs
    .map(l => `${formatLogTimestamp(l.timestamp)}\t${l.message}`)
    .join('\n');
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `instance-logs-${instanceId.slice(0, 8)}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

const LEVEL_FILTERS: LevelFilter[] = ['ALL', 'ERROR', 'WARN', 'INFO', 'DEBUG'];

const InstanceLogs: React.FC<InstanceLogsProps> = ({ kgId, versionId, instanceId, onClose }) => {
  const [logs, setLogs] = useState<LogEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextForwardToken, setNextForwardToken] = useState<string | null>(null);
  const [nextBackwardToken, setNextBackwardToken] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('ALL');
  const [copiedId, setCopiedId] = useState(false);

  const logsEndRef = useRef<HTMLDivElement>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);

  // Parse and filter — only collapse progress lines once loading is complete
  // During streaming, show raw logs so user sees live progress
  const parsed = useMemo(() => loading ? logs.map(ev => ({
    raw: ev, level: parseLevel(ev.message), message: ev.message, timestamp: ev.timestamp,
  })) : parseLogs(logs), [logs, loading]);

  const filtered = useMemo(() => {
    return parsed.filter(p => {
      if (levelFilter !== 'ALL' && p.level !== levelFilter) return false;
      if (searchTerm) {
        const t = searchTerm.toLowerCase();
        return (
          p.message.toLowerCase().includes(t) ||
          formatLogTimestamp(p.timestamp).toLowerCase().includes(t)
        );
      }
      return true;
    });
  }, [parsed, searchTerm, levelFilter]);

  const matchCount = useMemo(() => {
    if (!searchTerm) return 0;
    return filtered.length;
  }, [filtered, searchTerm]);

  const firstErrorIdx = useMemo(() => {
    return filtered.findIndex(p => p.level === 'ERROR');
  }, [filtered]);

  const levelCounts = useMemo(() => {
    const counts: Record<LogLevel, number> = { INFO: 0, WARN: 0, ERROR: 0, DEBUG: 0, OTHER: 0 };
    parsed.forEach(p => { counts[p.level] = (counts[p.level] || 0) + 1; });
    return counts;
  }, [parsed]);

  const scrollToBottom = useCallback(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const scrollToFirstError = useCallback(() => {
    const container = logsContainerRef.current;
    if (!container || firstErrorIdx < 0) return;
    const rows = container.querySelectorAll('.il-log-row');
    const target = rows[firstErrorIdx] as HTMLElement;
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [firstErrorIdx]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      let token: string | undefined = undefined;
      let allEvents: LogEvent[] = [];
      let prevToken: string | undefined = undefined;
      while (true) {
        const response: any = await apiService.getInstanceLogs(kgId, versionId, instanceId, token);
        if (!response) break;
        const events: LogEvent[] = response.events || [];
        allEvents = [...allEvents, ...events];
        setLogs([...allEvents]);
        const fwd: string | null = response.nextForwardToken || null;
        setNextForwardToken(fwd);
        setNextBackwardToken(response.nextBackwardToken || null);
        // Stop if: no token, token unchanged (end of stream), or no new events returned
        if (!fwd || fwd === prevToken || events.length === 0) break;
        prevToken = token;
        token = fwd;
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load logs'));
    } finally {
      setLoading(false);
    }
  };

  const loadMoreLogs = async (manual = true) => {
    if (!nextForwardToken) return;
    try {
      const response: any = await apiService.getInstanceLogs(kgId, versionId, instanceId, nextForwardToken);
      if (response) {
        const events = response.events || [];
        const fwd: string | null = response.nextForwardToken || null;
        // Only advance if the token changed and we got new events
        if (events.length > 0 && fwd !== nextForwardToken) {
          setLogs(prev => [...prev, ...events]);
          setNextForwardToken(fwd);
          setNextBackwardToken(response.nextBackwardToken || null);
        } else {
          // At end of stream — clear the token so auto-refresh stops polling
          if (fwd === nextForwardToken) setNextForwardToken(null);
        }
      }
    } catch (err: any) {
      if (manual) setError(extractErrorMessage(err, 'Failed to load more logs'));
    }
  };

  const loadPreviousLogs = async () => {
    if (!nextBackwardToken) return;
    const container = logsContainerRef.current;
    const prevH = container?.scrollHeight || 0;
    try {
      const response: any = await apiService.getInstanceLogs(kgId, versionId, instanceId, nextBackwardToken);
      if (response) {
        setLogs(prev => [...(response.events || []), ...prev]);
        setNextForwardToken(response.nextForwardToken || null);
        setNextBackwardToken(response.nextBackwardToken || null);
        setTimeout(() => {
          if (container) container.scrollTop = container.scrollHeight - prevH;
        }, 0);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load previous logs'));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLogs(); }, [kgId, versionId, instanceId]);

  // Auto-refresh every 30s — append any new logs
  useEffect(() => {
    const interval = setInterval(() => { loadMoreLogs(false); }, 30_000);
    return () => clearInterval(interval);
  }, [nextForwardToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopyId = () => {
    copyToClipboard(instanceId);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 1500);
  };

  return (
    <div className="il-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="il-shell">

        {/* ── Header (56px dark) ─────────────────────────────────────── */}
        <div className="il-header">
          <div className="il-header-left">
            <svg className="il-header-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
              <rect x="9" y="3" width="6" height="4" rx="1"/>
              <line x1="9" y1="12" x2="15" y2="12"/>
              <line x1="9" y1="16" x2="13" y2="16"/>
            </svg>
            <span className="il-header-title">Instance Logs</span>
            <button className="il-id-chip" onClick={handleCopyId} title="Copy instance ID">
              <span className="il-id-text">{instanceId.slice(0, 20)}{instanceId.length > 20 ? '…' : ''}</span>
              {copiedId
                ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              }
            </button>
          </div>

          <div className="il-header-right">
            {/* Search */}
            <div className="il-search-wrap">
              <svg className="il-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="il-search"
                type="text"
                placeholder="Search logs…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <span className="il-search-count">{matchCount}</span>
              )}
              {searchTerm && (
                <button className="il-search-clear" onClick={() => setSearchTerm('')}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
            </div>

            {/* Jump to error */}
            {firstErrorIdx >= 0 && (
              <button className="il-icon-btn il-icon-btn--error" onClick={scrollToFirstError} title="Jump to first error">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </button>
            )}

            {/* Download */}
            <button
              className="il-icon-btn"
              onClick={() => downloadLogs(logs, instanceId)}
              title="Download logs"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </button>

            {/* Refresh */}
            <button className="il-icon-btn" onClick={() => loadLogs()} title="Refresh">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
            </button>

            {/* Close */}
            <button className="il-icon-btn il-icon-btn--close" onClick={onClose} title="Close">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* ── Meta strip (36px) ──────────────────────────────────────── */}
        <div className="il-meta">
          <div className="il-meta-left">
            {loading && <span className="il-meta-spinner" />}
            <span className="il-meta-stat">
              {searchTerm
                ? <><strong>{matchCount}</strong> of {logs.length} events</>
                : <><strong>{logs.length}</strong> events</>
              }
            </span>
            {levelCounts.ERROR > 0 && <span className="il-meta-badge il-meta-badge--error">{levelCounts.ERROR} errors</span>}
            {levelCounts.WARN  > 0 && <span className="il-meta-badge il-meta-badge--warn">{levelCounts.WARN} warnings</span>}
          </div>
          <div className="il-meta-right">
            {nextBackwardToken && (
              <button className="il-meta-link" onClick={loadPreviousLogs}>
                ↑ Load earlier logs
              </button>
            )}
            {nextForwardToken && (
              <button className="il-meta-link" onClick={() => loadMoreLogs(true)}>
                ↓ Load more
              </button>
            )}
          </div>
        </div>

        {error && <div className="il-error-bar">{error}</div>}

        {/* ── Log body ───────────────────────────────────────────────── */}
        <div
          className="il-body"
          ref={logsContainerRef}
        >
          {loading && logs.length === 0 ? (
            <div className="il-center-state">
              <div className="il-spinner" />
              <span>Loading logs…</span>
            </div>
          ) : logs.length === 0 ? (
            <div className="il-center-state">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.3 }}>
                <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
                <rect x="9" y="3" width="6" height="4" rx="1"/>
              </svg>
              <span>No logs available</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="il-center-state">
              <span>No logs match the current filter</span>
            </div>
          ) : (
            <div className="il-log-list">
              {filtered.map((log, idx) => (
                <LogRow key={`${log.timestamp}-${idx}`} log={log} searchTerm={searchTerm} />
              ))}
              <div ref={logsEndRef} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Log Row component ────────────────────────────────────────────────────────

interface LogRowProps {
  log: ParsedLog;
  searchTerm: string;
}

const LogRow: React.FC<LogRowProps> = React.memo(({ log, searchTerm }) => {
  const [expanded, setExpanded] = useState(false);

  const ts = formatLogTimestamp(log.timestamp);

  return (
    <div
      className={`il-log-row il-log-row--${log.level.toLowerCase()} ${expanded ? 'il-log-row--open' : ''}`}
      onClick={() => setExpanded(e => !e)}
    >
      <span className={`il-log-rail il-log-rail--${log.level.toLowerCase()}`} />
      <span className="il-log-ts">{ts}</span>
      <span className={`il-log-lvl il-log-lvl--${log.level.toLowerCase()}`}>{log.level}</span>
      <span className="il-log-msg">
        {log.collapsed != null
          ? <span className="il-collapsed-badge">+{log.collapsed} progress lines</span>
          : highlight(log.message, searchTerm)
        }
      </span>
    </div>
  );
});

LogRow.displayName = 'LogRow';

export default InstanceLogs;
